<script lang="ts" setup>
import type { ChatbotComponentProps } from '~/components/chat/ChatbotComponentProps'
import { useI18n } from 'vue-i18n'
import { computed, defineAsyncComponent, ref, useTemplateRef, watch } from 'vue'
import ChatbotMessage from '~/components/chat/ChatbotMessage.vue'
import ChatbotInput from '~/components/chat/ChatbotInput.vue'
import ChatbotInfo from '~/components/chat/ChatbotInfo.vue'
import ChatbotFeedback from '~/components/chat/ChatbotFeedback.vue'

const props = defineProps<ChatbotComponentProps>()
const ChatbotMathInput = defineAsyncComponent(() =>
  import('~/components/chat/ChatbotMathInput.vue')
)

const initialized = ref(false)
const { t } = useI18n()

const inputComponent = useTemplateRef<{ focus: () => void }>('inputComponent')

const screenReaderMessage = computed(() => props.isStreaming
  ? t('chatbot.answering')
  : t('chatbot.answered'))

watch(props, () => {
  if (props.bot && !initialized.value && !props.isLoading) {
    props.prompt(props.options.prompt, true)
    initialized.value = true
  }

  if (!props.autoFocus || props.isStreaming) {
    return
  }

  setTimeout(() => inputComponent?.value?.focus(), 200)
}, { immediate: true })
</script>

<template>
  <section
    class="chatbot motion-safe:animate-fade mx-auto grid grid-rows-1"
    :aria-label="t('labels.chatbot')"
    data-ignore-tts
  >
    <div
      class="sr-only"
      aria-live="assertive"
      role="log"
    >
      {{ screenReaderMessage }}
    </div>
    <ChatbotInfo />
    <div
      v-if="isLoading"
      v-text="t('loading')"
    />
    <div
      v-else
      class="grid min-h-0 min-w-0 grid-rows-[1fr_max-content] gap-4"
    >
      <div class="min-h-0 min-w-0">
        <div class="grid auto-rows-max gap-4">
          <template
            v-for="(message, index) in chatHistory"
            :key="index"
          >
            <ChatbotMessage
              :message="message"
              :auto-focus="autoFocus"
              :show-recall-notice="((chatHistory.length - 1) - index) === options.maxRecall"
            />
          </template>
        </div>
      </div>
      <ChatbotMathInput
        v-if="bot?.mathModeEnabled"
        ref="inputComponent"
        v-bind="props"
      />
      <ChatbotInput
        v-else
        ref="inputComponent"
        :bot="bot"
        :prompt="prompt"
        :is-loading="isLoading"
        :is-streaming="isStreaming"
        :is-interacting="isInteracting"
      />
      <ChatbotFeedback
        :chat-history
        :location-id="bot?.locationId"
      />
      <div
        v-if="isError"
        v-text="t('chatbot.error')"
      />
    </div>
  </section>
</template>
